import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators';

import { ifDefined } from 'lit/directives/if-defined';
import topicData from '../../helpers/state/topic-data';
import { validateEmail } from '../../helpers/strings';

@customElement('form-input')
export class FormInput extends LitElement {
    @query('#formInput')
    formInputElement!: HTMLInputElement;

    @property({ type: String })
    value?: string;

    @property({ type: String })
    type?: string;

    @property({ type: String })
    label?: string;

    @property({ type: String })
    placeholder?: string;

    @property({ type: String })
    prefix: string= '';

    @property({ type: String })
    prefixIcon?: string= '';

    @property({ type: String })
    maxLength?: string;

    @property({ type: String })
    topLabel?: string;

    @property({ type: Boolean })
    required?: boolean;

    @property({ type: Boolean })
    hideMaxLength?: boolean;

    @property({ type: Boolean })
    noBorder?: boolean;

    @property({ type: Boolean })
    hasAdd?: boolean;

    @property({ type: Boolean })
    disabled: boolean = false;

    get hasPrefix() {
        return this.prefix.length > 0;
    }

    private handleChange(e: Event) {
        this.value = (e.target as HTMLInputElement).value;
        this.dispatchEvent(
            new CustomEvent('change', {
                detail: e.target,
            })
        );
    }

    private handleInputSubmit(e: Event) {
        e.preventDefault();
        this.dispatchEvent(
            new CustomEvent('submit', {
                detail: this.formInputElement.value,
            })
        );
        return false;
    }

    reportValidity() {
        if (this.type === 'email' && this.value !== undefined) {
            return validateEmail(this.value);
        }
        return this.formInputElement?.reportValidity();
    }

    protected render() {
        const maxLimit = this.topLabel ? this.topLabel : this.maxLength ? `${this.maxLength} chars` : '';

        return html`
            <form @submit="${this.handleInputSubmit}" style="font-family:${topicData.topic?.custom_font ?? 'Montserrat'}">
                <label class="form-label">
                    <span>${this.label}</span>
                    ${this.hideMaxLength === true ? '' : html`
                        <span class="max-limit">${maxLimit}</span>
                    `}
                </label>
                <div class="form-input__wrapper">
                    ${this.prefixIcon ? html`<mwc-icon class="form-input__prefix__icon">${this.prefixIcon}</mwc-icon>` : ''}
                    ${this.hasPrefix ? html`<span class="form-input__prefix">${this.prefix}</span>` : ''}
                    <input
                        id="formInput"
                        name="formInput"
                        class="form-control  ${!this.hasPrefix ? 'form-control--rounded' : ''} ${this.noBorder === true ? 'form-control--no-border' : ''} ${this.disabled ? 'form-control--disabled' : ''} ${this.prefixIcon ? 'form-control--prefix-icon' : ''}"
                        style="font-family:${topicData.topic?.custom_font ?? 'Montserrat'}"
                        type="${this.type}"
                        value="${ifDefined(this.value)}"
                        placeholder="${ifDefined(this.placeholder)}"
                        maxlength="${this.maxLength}"
                        ?required=${this.required}
                        ?disabled=${this.disabled}
                        @change="${this.handleChange}"/>
                    ${this.hasAdd === true ? html`
                        <div class="form-input__add" @click="${this.handleInputSubmit}">
                            <mwc-icon>add</mwc-icon>
                        </div>
                    ` : ''}
                </div>
            </form>
        `;
    }

    static get styles() {
        return css`
            .form-input__prefix {
                font-weight: 800;
                font-size: 18px;
                align-self: center;
                padding: 10px 6px;
                background: var(--theme-lightest-color);
                border: 1px solid var(--theme-input-border-color);
                border-right: none;
                border-top-left-radius: 4px;
                border-bottom-left-radius: 4px;
            }

            .form-input__wrapper {
                display: flex;
                position: relative;
            }

            .form-label {
                line-height: 1rem;
                font-size: 0.8rem;
                font-weight: 500;
                color: var(--theme-input-color);
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
            }

            .form-label .max-limit {
                color: var(--theme-input-color);
                font-size: 0.8rem;
                font-weight: normal;
                padding-left: 1rem;
            }

            .form-control {
                display: block;
                width: 100%;
                box-sizing: border-box;
                padding: 10px;
                font-size: 0.75rem;
                line-height: 1.5;
                color: var(--theme-input-color);
                border: 1px solid var(--theme-input-border-color);
                background-color: var(--theme-white-color);
                background-clip: padding-box;
                -webkit-appearance: none;
                -moz-appearance: none;
                appearance: none;
                border-top-right-radius: 6px;
                border-bottom-right-radius: 6px;
                box-shadow: inset 0 1px 2px transparent;
                transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out,
                    box-shadow 0.15s ease-in-out;
            }

            .form-control--rounded {
                border-top-left-radius: 6px;
                border-bottom-left-radius: 6px;
            }

            .form-control--no-border {
                border: none;
            }

            input:-webkit-autofill {
                -webkit-box-shadow: 0 0 0 1000px white inset;
            }

            .form-control:focus {
                border-color: var(--theme-focus-border-color);
                background-color: var(var(--theme-white-color));
                outline: 0;
            }

            .form-control--disabled {
                background-color: var(--theme-lightest-color);
                opacity: 0.7;
            }

            .form-control--prefix-icon {
                padding-left: 2.5rem;
            }

            .form-control:invalid,
            .form-control.is-invalid {
                border-color: var(--main-invalid-color);
                padding-right: calc(1.5em + 1.15rem);
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' fill='%23EA8567' fill-rule='evenodd'%3e%3cpath d='M9 3a6 6 0 1 0 0 12A6 6 0 1 0 9 3zM1.5 9a7.5 7.5 0 1 1 15 0 7.5 7.5 0 1 1-15 0zM9 5.25a.75.75 0 0 1 .75.75v3a.75.75 0 1 1-1.5 0V6A.75.75 0 0 1 9 5.25zM8.25 12a.75.75 0 0 1 .75-.75h.008a.75.75 0 1 1 0 1.5H9a.75.75 0 0 1-.75-.75z'/%3e%3c/svg%3e");
                background-repeat: no-repeat;
                background-position: right calc(0.375em + 0.2875rem) center;
                background-size: calc(0.75em + 0.575rem) calc(0.75em + 0.575rem);
            }

            .form-control::placeholder {
                color: var(--theme-input-color);
                opacity: 1;
            }

            .form-input__add {
                position: absolute;
                cursor: pointer;
                top: 6px;
                right: 6px;
                padding: 0.25rem;
                --mdc-icon-size: 1.5rem;
                color: var(--main-primary-color);
            }

            .form-input__prefix__icon {
                position: absolute;
                left: 6px;
                top: 6px;
                padding: 0.25rem;
                --mdc-icon-size: 1.5rem;
                color: var(--theme-input-color);
            }
        `;
    }
}
